import { blue } from '@ant-design/colors';
import { CopyTwoTone } from '@ant-design/icons';
import {
  Alert,
  Col,
  Descriptions,
  Divider,
  Row,
  Skeleton,
  Space,
  Typography,
} from 'antd';
import dayjs from 'dayjs';
import React, { useContext, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { ENotificationSeverity } from '../../admin-types';
import CopyToClipboard from '../components/copy-to-clipboard';
import ExternalLink from '../components/external-link';
import Link from '../components/link';
import ProductAdd from '../components/product/product-add';
import { EDateFormat } from '../fragments/date-format';
import { useAppDispatch } from '../hooks/use-app-dispatch';
import { notificationContext } from '../notification';
import { type RootState } from '../reducers';
import { downloaderLoadProduct } from '../reducers/downloader/reducer';
import { SPACING } from '../theme/tokens';

function DownloaderProductContainer(): React.ReactElement {
  const dispatch = useAppDispatch();
  const downloaderProduct = useSelector((s: RootState) => s.Downloader.product);
  const productsNewProduct = useSelector(
    (s: RootState) => s.Products.newProduct,
  );

  const isFirstRender = useRef(true);
  const { productDownloadedUUID } = useParams();
  const { openNotification } = useContext(notificationContext);
  const product = downloaderProduct;

  useEffect((): void => {
    if (productDownloadedUUID) {
      dispatch(downloaderLoadProduct(productDownloadedUUID));
    }
  }, [dispatch, productDownloadedUUID]);

  useEffect((): void => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    openNotification('topRight', {
      severity: ENotificationSeverity.SUCCESS,
      title: 'Product updated!',
      description: (
        <>
          price: {downloaderProduct?.price}
          <br />
          stock: {downloaderProduct?.stock}
        </>
      ),
    });
  }, [downloaderProduct?.price, downloaderProduct?.stock, openNotification]);

  const productNameNormalized = useMemo((): string => {
    if (!product?.url) {
      return '';
    }
    try {
      const url = new URL(product.url);
      return url.pathname
        .replaceAll('/', '')
        .replaceAll('-', ' ')
        .replace(/([p\d]{6,})/gim, '')
        .trim();
    } catch {
      return '';
    }
  }, [product?.url]);

  const rawFormatted = useMemo((): string => {
    if (!product?.raw) {
      return '';
    }
    try {
      return JSON.stringify(JSON.parse(product.raw), null, 2);
    } catch {
      return product.raw;
    }
  }, [product?.raw]);

  if (product === undefined) {
    return <Skeleton active />;
  }

  if (product === null) {
    return (
      <Alert
        type="error"
        title="Product not found"
        description={`Product UUID: ${productDownloadedUUID}`}
        showIcon
      />
    );
  }

  const newProduct = productsNewProduct;

  return (
    <>
      <Descriptions
        title="Downloader product"
        bordered
        column={{ xxl: 4, xl: 4, lg: 4, md: 4, sm: 2, xs: 2 }}
        items={[
          {
            key: 'uuid',
            label: 'UUID',
            span: 1,
            children: <Typography.Text strong>{product.uuid}</Typography.Text>,
          },
          {
            key: 'domain',
            label: 'Domain',
            span: 1,
            children: (
              <Typography.Text strong>{product.domain}</Typography.Text>
            ),
          },
          {
            key: 'category',
            label: 'Category',
            span: 1,
            children: (
              <Typography.Text strong>{product.category}</Typography.Text>
            ),
          },
          {
            key: 'updated',
            label: 'Updated',
            span: 1,
            children: (
              <Typography.Text strong>
                {dayjs(product.updatedAt).format(EDateFormat.DATE_TIME)}
              </Typography.Text>
            ),
          },
          {
            key: 'url',
            label: 'URL',
            span: { xxl: 4, xl: 4, lg: 4, md: 4, sm: 2, xs: 2 },
            children: (
              <Space size={SPACING.lg} align="start">
                <Typography.Text strong>{product.url}</Typography.Text>
                <ExternalLink
                  to={product.url}
                  ariaLabel="Open product in new tab"
                />

                <CopyToClipboard text={product.url} type="link" />
              </Space>
            ),
          },
          {
            key: 'name',
            label: 'Name',
            span: { xxl: 4, xl: 4, lg: 4, md: 4, sm: 2, xs: 2 },
            children: (
              <Space size={SPACING.lg} align="start">
                <Typography.Text strong>
                  {productNameNormalized}
                </Typography.Text>

                <CopyToClipboard text={productNameNormalized}>
                  <CopyTwoTone twoToneColor={blue.primary} />
                </CopyToClipboard>
              </Space>
            ),
          },
          {
            key: 'product',
            label: 'Product',
            span: { xxl: 4, xl: 4, lg: 4, md: 4, sm: 2, xs: 2 },
            children: product.product?.uuid ? (
              <Link
                to={`/product/${product.product.uuid}`}
                label={product.product.uuid}
                openInNewTab
              />
            ) : (
              'Not yet created'
            ),
          },
          {
            key: 'price',
            label: 'Price',
            span: 1,
            children: <Typography.Text strong>{product.price}</Typography.Text>,
          },
          {
            key: 'stock',
            label: 'Stock',
            span: 1,
            children: <Typography.Text strong>{product.stock}</Typography.Text>,
          },
          {
            key: 'raw',
            label: 'Raw',
            span: 2,
            children: (
              <small>
                <pre>{rawFormatted}</pre>
              </small>
            ),
          },
        ]}
      />

      <Divider />

      {!product.product?.uuid && (
        <ProductAdd productDownloadedUUID={productDownloadedUUID} />
      )}

      {newProduct === null && (
        <Alert
          type="error"
          title="Product not created"
          description={`Product UUID: ${productDownloadedUUID}`}
          showIcon
        />
      )}

      {newProduct && (
        <Row>
          <Col span={10} offset={8}>
            <Link
              to={`/product/${newProduct.uuid}`}
              openInNewTab
              label={`Created new product with UUID ${newProduct.uuid}`}
            />
          </Col>
        </Row>
      )}
    </>
  );
}

export default DownloaderProductContainer;
